import { calculateDrawings, completedUpto } from './typeOfWork.calculations';
import {
  GROUND_LEVEL_TYPE,
  GROUND_LEVEL_USAGE,
  WORK_AGE,
  WORK_CATEGORY,
} from './work.constants';

/**
 * Turns the Type of Work answers into the building's floors, top to bottom, so they can be
 * drawn as an elevation.
 *
 * This is presentation only — it never feeds a save. It reads the same fields as
 * `jobFloors.mapper`, but that mapper groups floors into `job_floors` rows by type and
 * counts, which is the wrong shape to draw: two basements are one row there and two
 * stacked bands here.
 */

export type FloorBandKind =
  | 'staircase'
  | 'additional'
  | 'roof'
  | 'upper'
  | 'mezzanine'
  | 'ground'
  | 'parking'
  | 'basement'
  | 'foundation';

export interface FloorBand {
  /** Stable across re-renders so React can key on it. */
  key: string;
  /** What the band is called on the drawing, e.g. "Floor 3", "Lower Ground", "Basement 2". */
  name: string;
  /** Secondary line: the upper-floor category name, or the ground level's usage. */
  detail?: string;
  kind: FloorBandKind;
  /** Marks floors that already exist on site, so new work reads differently from old. */
  isExisting?: boolean;
  /** The structural drawings for this level, e.g. "S3", "S1–S2", "C1–C2". */
  drawings?: string;
}

const num = (value: unknown): number => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

/** Guards against a typo in "total floors" rendering thousands of bands. */
const MAX_BANDS_PER_GROUP = 60;

const usageLabel = (usage: unknown): string | undefined => {
  if (usage === GROUND_LEVEL_USAGE.PARKING) return 'Parking';
  if (usage === GROUND_LEVEL_USAGE.GROUND) return 'Ground floor';
  return undefined;
};

/** The category a floor number was assigned to in the Upper Floors section, if any. */
function groupNameFor(groups: any[], floorNumber: number): string | undefined {
  const match = groups.find(
    (group) => Array.isArray(group?.floors) && group.floors.includes(floorNumber)
  );
  const name = typeof match?.name === 'string' ? match.name.trim() : '';
  return name || undefined;
}

export function buildFloorStack(values: Record<string, any>): FloorBand[] {
  const isBuilding =
    values.workCategory === WORK_CATEGORY.BUILDING ||
    values.workCategory === WORK_CATEGORY.ALLIED_BUILDING;

  // Allied-only work has no building to draw.
  if (!isBuilding) return [];

  const bands: FloorBand[] = [];

  const upperCount = Math.min(num(values.upperFloorsCount), MAX_BANDS_PER_GROUP);
  const groups = Array.isArray(values.upperFloorGroups) ? values.upperFloorGroups : [];

  /**
   * On existing-work jobs the building already stands up to "Work Completed Upto" and the new
   * work goes on top. Floors 1 to that one are existing, and so are the ground level and the
   * basements under them — the same reading the drawing maths uses, where only the floors
   * above it, the roof and what sits on the roof count as new slabs.
   */
  const isExistingWork = values.workAge === WORK_AGE.EXISTING;
  const existingCount = isExistingWork ? Math.min(completedUpto(values) ?? 0, upperCount) : 0;

  // ── Staircase cap / OHWT / LMR, above everything else ─────────────────────
  // They sit on the roof, so they are drawn at the top of the stack, inset like a mezzanine
  // because none of them covers the whole footprint.
  if (values.hasStaircaseOhwt !== false) {
    const caps = Math.min(num(values.staircaseOhwtCount), MAX_BANDS_PER_GROUP);
    for (let i = caps; i >= 1; i -= 1) {
      bands.push({
        key: `staircase-${i}`,
        name: 'Staircap / OHWT / LMR',
        detail: caps > 1 ? `${i} of ${caps}` : undefined,
        kind: 'staircase',
      });
    }
  }

  // ── Provision for additional floors, above the built floors ───────────────
  if (values.hasAdditionalFloors) {
    const additional = Math.min(num(values.additionalFloorsCount), MAX_BANDS_PER_GROUP);
    for (let i = additional; i >= 1; i -= 1) {
      bands.push({
        key: `additional-${i}`,
        name: `Additional Floor ${i}`,
        detail: 'Provision',
        kind: 'additional',
      });
    }
  }

  // ── The roof slab over the top floor ──────────────────────────────────────
  bands.push({ key: 'roof', name: 'Roof', kind: 'roof' });

  // ── Upper floors, highest first ───────────────────────────────────────────
  for (let floor = upperCount; floor >= 1; floor -= 1) {
    bands.push({
      key: `upper-${floor}`,
      name: `Floor ${floor}`,
      detail: groupNameFor(groups, floor),
      kind: 'upper',
      isExisting: floor <= existingCount,
    });
  }

  // ── Ground level ──────────────────────────────────────────────────────────
  // A mezzanine belongs to a ground-floor level only; a parking level has none, whatever an
  // earlier answer left in the form.
  const takesMezzanine = (usage: unknown) => usage === GROUND_LEVEL_USAGE.GROUND;

  if (values.groundLevelType === GROUND_LEVEL_TYPE.SINGLE) {
    if (values.singleHasMezzanine && takesMezzanine(values.singleGroundUsage)) {
      bands.push({ key: 'mezzanine-single', name: 'Mezzanine', kind: 'mezzanine', isExisting: isExistingWork });
    }
    bands.push({
      key: 'ground-single',
      name: values.singleGroundUsage === GROUND_LEVEL_USAGE.PARKING ? 'Parking' : 'Ground Floor',
      detail: usageLabel(values.singleGroundUsage),
      kind: values.singleGroundUsage === GROUND_LEVEL_USAGE.PARKING ? 'parking' : 'ground',
      isExisting: isExistingWork,
    });
  }

  if (values.groundLevelType === GROUND_LEVEL_TYPE.MULTIPLE) {
    if (values.upperGroundHasMezzanine && takesMezzanine(values.upperGroundUsage)) {
      bands.push({
        key: 'mezzanine-upper-ground',
        name: 'Mezzanine',
        detail: 'Upper ground',
        kind: 'mezzanine',
        isExisting: isExistingWork,
      });
    }
    bands.push({
      key: 'ground-upper',
      name: 'Upper Ground',
      detail: usageLabel(values.upperGroundUsage),
      kind: values.upperGroundUsage === GROUND_LEVEL_USAGE.PARKING ? 'parking' : 'ground',
      isExisting: isExistingWork,
    });

    if (values.lowerGroundHasMezzanine && takesMezzanine(values.lowerGroundUsage)) {
      bands.push({
        key: 'mezzanine-lower-ground',
        name: 'Mezzanine',
        detail: 'Lower ground',
        kind: 'mezzanine',
        isExisting: isExistingWork,
      });
    }
    bands.push({
      key: 'ground-lower',
      name: 'Lower Ground',
      detail: usageLabel(values.lowerGroundUsage),
      kind: values.lowerGroundUsage === GROUND_LEVEL_USAGE.PARKING ? 'parking' : 'ground',
      isExisting: isExistingWork,
    });
  }

  // ── Basements, shallowest first so B1 sits above B2 ───────────────────────
  if (values.hasBasement) {
    const basements = Math.min(num(values.basementCount), MAX_BANDS_PER_GROUP);
    for (let i = 1; i <= basements; i += 1) {
      bands.push({
        key: `basement-${i}`,
        name: `Basement ${i}`,
        kind: 'basement',
        isExisting: isExistingWork,
      });
    }
  }

  // ── Column footing drawings, under everything ─────────────────────────────
  const drawings = calculateDrawings(values);
  if (drawings.columnRange) {
    bands.push({ key: 'foundation', name: 'Foundation', kind: 'foundation', drawings: dash(drawings.columnRange) });
  }

  numberSlabs(bands, values, drawings);
  return bands;
}

const dash = (range: string) => range.replace('-', '–');

/** The usage of the ground level a ground or mezzanine band belongs to. */
function groundUsage(key: string, values: Record<string, any>) {
  if (key.endsWith('single')) return values.singleGroundUsage;
  if (key.endsWith('upper') || key.endsWith('upper-ground')) return values.upperGroundUsage;
  return values.lowerGroundUsage;
}

/**
 * Puts the slab numbers S1, S2, … on the levels they belong to.
 *
 * The count walks up the building the same way `calculateDrawings` adds it up: each basement
 * (or the lower ground's column footing when there is no basement), each ground level and its
 * mezzanine (a parking level has no mezzanine slab), each upper floor, the roof, then each
 * staircase cap / OHWT / LMR. Provision floors are not drawn yet and take no number. Existing
 * levels keep their place in the count but show no number, since only the new slabs are drawn
 * — which is where `slabRange` starts on existing-work jobs.
 */
function numberSlabs(
  bands: FloorBand[],
  values: Record<string, any>,
  drawings: ReturnType<typeof calculateDrawings>
) {
  let next = 1;
  const take = (count: number) => {
    const first = next;
    next += count;
    return count === 0 ? '' : count === 1 ? `S${first}` : `S${first}–S${next - 1}`;
  };
  const label = (band: FloorBand, count: number) => {
    const numbers = take(count);
    if (numbers && !band.isExisting) band.drawings = numbers;
  };

  // A lower-ground column footing without a basement is drawn as its own slab, at the bottom.
  const footingSlab = !values.hasBasement && drawings.basementDrawings > 0 ? drawings.basementDrawings : 0;
  let footingPlaced = footingSlab === 0;

  // Bottom of the stack first.
  for (const band of [...bands].reverse()) {
    switch (band.kind) {
      case 'basement':
      case 'upper':
      case 'roof':
      case 'staircase':
        label(band, 1);
        break;
      case 'ground':
      case 'parking': {
        const extra = footingPlaced ? 0 : footingSlab;
        footingPlaced = true;
        // A level whose usage is not chosen yet is not counted by the drawing maths either.
        label(band, extra + (groundUsage(band.key, values) ? 1 : 0));
        break;
      }
      case 'mezzanine': {
        // Only a ground (not parking) level's mezzanine is its own slab.
        const usage = groundUsage(band.key, values);
        label(band, usage && usage !== GROUND_LEVEL_USAGE.PARKING ? 1 : 0);
        break;
      }
      default:
        break;
    }
  }
}

/**
 * Slab numbers beyond what the levels account for — when "No. of Slabs" was set higher than
 * the building needs. They are real drawings but belong to no one level, so the drawing lists
 * them separately rather than guessing a floor.
 */
export function extraSlabs(values: Record<string, any>): string {
  const drawings = calculateDrawings(values);
  if (drawings.slabsCount <= drawings.minSlabs) return '';
  const first = drawings.minSlabs + 1;
  return first === drawings.slabsCount ? `S${first}` : `S${first}–S${drawings.slabsCount}`;
}
